import { FC, ReactElement, useEffect, useState } from 'react';

interface SlideProps {
  contents: any[];
  renderSlide: (
    state?: any,
    index?: number,
    condition?: boolean,
    activeIndex?: number,
  ) => ReactElement<any, any>;
  renderIndex?: (state?: any, index?: number) => ReactElement<any, any>;
  showIndex?: boolean;
  touchSlidePixel?: number;
  slideTransitionDuration?: number;
  currentSlideDuration?: number;
  indexContainerClass?: string;
  indexContainerStyle?: React.CSSProperties;
  activeIndexClass?: string;
  inactiveIndexClass?: string;
  indexListStyle?: (i?: number) => React.CSSProperties;
}
const Slide: FC<SlideProps> = ({
  contents,
  renderSlide,
  renderIndex,
  showIndex = false,
  touchSlidePixel,
  slideTransitionDuration,
  currentSlideDuration,
  indexContainerClass,
  indexContainerStyle,
  activeIndexClass,
  inactiveIndexClass,
  indexListStyle,
}) => {
  const TOUCH_SLIDE_PIXEL = touchSlidePixel ?? 50;
  const SLIDE_TRANSITION_DURATION = slideTransitionDuration ?? 400; // 다음 슬라이드로 넘어가는 트랜지션 시간
  const CURRENT_SLIDE_DURATION = currentSlideDuration ?? 4000; // 현재 슬라이드 지속 시간

  const [activeIndex, setActiveIndex] = useState(1); // 0번인덱스는 products 의 가장 마지막 요소의 복사본
  const [slideCards, setSlideCards] = useState<any[]>([]);
  const [isMovingFirstSlde, setIsMovingFirstSlide] = useState(true);
  const [isMovingLastSlide, setIsMovingLastSlide] = useState(true);
  const [startX, setStartX] = useState(0);
  const [endX, setEndX] = useState(0);

  const isMovingStartOrEndSlide = isMovingFirstSlde || isMovingLastSlide;

  const onTouchStart = (e: React.TouchEvent) => {
    // 터치 시작시 터치한 좌표 기억
    setStartX(e.touches[0].clientX);
    setEndX(0);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    // 터치 종료시 좌표 기억
    setEndX(e.changedTouches[0].clientX);
  };

  const onChangeActiveIndex = (index: number) => () => {
    setActiveIndex(index);
  };

  // 터치 시작좌표와 종료좌표가 있을 때 터치한 방향으로 슬라이드 전환
  useEffect(() => {
    if (contents.length <= 1) return;
    if (startX === 0 || endX === 0) return;
    if (startX > endX + TOUCH_SLIDE_PIXEL) {
      setActiveIndex((prevIndex) => {
        return (prevIndex + 1) % slideCards.length;
      });
    } else if (endX > startX + TOUCH_SLIDE_PIXEL) {
      setActiveIndex((prevIndex) => {
        return (prevIndex - 1) % slideCards.length;
      });
    }
    setStartX(0); // 슬라이드 전환시 기억한 좌표 지움
    setEndX(0);
  }, [startX, endX, slideCards.length, contents.length]);

  useEffect(() => {
    if (!contents.length || contents.length === 1) return;
    setSlideCards([contents[contents.length - 1], ...contents, contents[0]]);
  }, [contents]);

  useEffect(() => {
    if (contents.length <= 1) return;
    const timeout: NodeJS.Timeout = setTimeout(() => {
      setActiveIndex((prev) => prev + 1);
    }, CURRENT_SLIDE_DURATION);

    return () => clearTimeout(timeout);
  }, [activeIndex, contents.length]);

  useEffect(() => {
    if (contents.length <= 1) return;
    let timeout: NodeJS.Timeout;
    // 마지막 슬라이드(첫번째 product 의 복사본)의 경우
    // 슬라이드 트랜지션 시간 이후 다시 첫번째 product 슬라이드로 돌림
    if (activeIndex === slideCards.length - 1) {
      timeout = setTimeout(() => {
        setActiveIndex(1);
        setIsMovingFirstSlide(true);
      }, SLIDE_TRANSITION_DURATION);
    }
    // 첫번째 슬라이드(마지막 product 의 복사본)의 경우
    // 슬라이드 트랜지션 시간 이후 다시 마지막 product 슬라이드로 돌림
    if (activeIndex === 0) {
      timeout = setTimeout(() => {
        setActiveIndex(slideCards.length - 2);
        setIsMovingLastSlide(true);
      }, SLIDE_TRANSITION_DURATION);
    }
    return () => clearTimeout(timeout);
  }, [activeIndex, slideCards.length, contents.length]);

  useEffect(() => {
    if (contents.length <= 1) return;
    // isMovingFirst(Last)Slide 가 true 가 되도 곧 false 로 바꿈
    const timeout = setTimeout(() => {
      setIsMovingFirstSlide(false);
      setIsMovingLastSlide(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [isMovingFirstSlde, isMovingLastSlide, contents.length]);

  if (contents.length <= 0) return <></>;

  return (
    <div className="overflow-hidden rounded-sm">
      {contents.length === 1 ? (
        renderSlide(contents[0])
      ) : (
        <div>
          <div
            className="flex"
            style={{
              width: `${slideCards.length * 100}%`,
              transform: `translateX(-${
                (activeIndex * 100) / slideCards.length
              }%)`,
              transition:
                isMovingFirstSlde || isMovingLastSlide
                  ? ''
                  : `transform ${SLIDE_TRANSITION_DURATION}ms ease`,
              flex: '1 0 100%',
            }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {slideCards.map((content, i) =>
              renderSlide(content, i, isMovingStartOrEndSlide, activeIndex),
            )}
          </div>
        </div>
      )}
      {showIndex && (
        <ul className={indexContainerClass} style={indexContainerStyle}>
          {contents.length > 1 &&
            contents.map((content, i) => (
              <li
                key={i}
                onClick={onChangeActiveIndex(i + 1)}
                className={
                  activeIndex === i + 1 || // 현재 인덱스와 일치할 때
                  (activeIndex === slideCards.length - 1 && i === 0) || // activeIndex 가 가장 마지막일 때 가장 앞 인덱스를 활성화
                  (activeIndex === 0 && i === contents.length - 1) // activeIndex 가 0일 때 가장 마지막 인덱스를 활성화
                    ? activeIndexClass
                    : inactiveIndexClass
                }
                style={indexListStyle ? indexListStyle(i) : undefined}
              >
                {renderIndex && renderIndex(content)}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default Slide;
